import { Bot } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Breadcrumb, type Crumb } from './Breadcrumb';

/** Sets the browser document title from the canonical page title (screens §1). */
export function usePageTitle(title: string | null | undefined) {
  useEffect(() => {
    if (title) document.title = `${title} — Lixionary Jira`;
  }, [title]);
}

export function PageHeader({
  title,
  subtitle,
  actions,
  crumbs,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  crumbs?: Crumb[];
  eyebrow?: ReactNode;
}) {
  return (
    <div className="stack sm">
      {crumbs && <Breadcrumb items={crumbs} />}
      <div className="page-header">
        <div className="titles">
          {eyebrow}
          <h1 className="page-title">{title}</h1>
          {subtitle && <span className="muted">{subtitle}</span>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
    </div>
  );
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('') || '?';

export function Avatar({ name, kind, small }: { name: string; kind?: string; small?: boolean }) {
  return (
    <span className={`avatar${small ? ' sm' : ''}${kind === 'ai_service' ? ' ai' : ''}`} aria-hidden>
      {kind === 'ai_service' ? <Bot size={small ? 12 : 16} /> : initials(name)}
    </span>
  );
}

export interface ActorRef {
  kind: 'human' | 'ai_service' | 'system';
  id?: string | null;
  name: string;
}

export function ActorName({ actor }: { actor: ActorRef | null | undefined }) {
  if (!actor) return <span className="muted">—</span>;
  return (
    <span>
      {actor.name}
      {actor.kind === 'ai_service' && <span className="muted small"> (AI)</span>}
      {actor.kind === 'system' && <span className="muted small"> (system)</span>}
    </span>
  );
}

export function Card({
  title,
  subtitle,
  action,
  children,
  flat,
  tight,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  flat?: boolean;
  tight?: boolean;
}) {
  return (
    <section className={`card${flat ? ' flat' : ''}${tight ? ' tight' : ''}`}>
      {(title || action) && (
        <div className="card-header">
          <div className="stack" style={{ gap: 2 }}>
            {title && <h2 className="card-title">{title}</h2>}
            {subtitle && <span className="card-subtitle">{subtitle}</span>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function KeyValue({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v ?? <span className="muted">—</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export const Mono = ({ children }: { children: ReactNode }) => <span className="mono">{children}</span>;

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  tabs: { value: T; label: string; count?: number | undefined }[];
  label: string;
}) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.value}
          role="tab"
          id={`tab-${t.value}`}
          aria-selected={t.value === value}
          tabIndex={t.value === value ? 0 : -1}
          className="tab"
          onClick={() => onChange(t.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]!;
              onChange(next.value);
              document.getElementById(`tab-${next.value}`)?.focus();
            }
          }}
        >
          {t.label}
          {t.count !== undefined && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Keyset "Load more" pagination control. */
export function LoadMore({
  hasMore,
  onClick,
  loading,
}: {
  hasMore: boolean;
  onClick: () => void;
  loading?: boolean;
}) {
  if (!hasMore) return null;
  return (
    <div className="pagination">
      <button className="btn btn-secondary sm" onClick={onClick} disabled={loading}>
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}
